import React, { useEffect, useState } from 'react';

import { AccordionItem } from '@radix-ui/react-accordion';
import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';
import { useMeasure } from 'react-use';

import { ReactComponent as CloseButton } from '@pkg/assets/icons/v2/close-circle.svg';
import { ReactComponent as SearchIcon } from '@pkg/assets/icons/v2/search.svg';
import { Button } from '@pkg/components/Sections/Profile/Buttons';
import { FilterContent, FilterHeading, type FilterIndex, FilterLink } from '@pkg/components/Sections/Profile/Filters';
import { DepInput } from '@pkg/components/deprecated';
import { getPagesContent, GROUPS_SEARCH_PARAMS_KEYS, useGetGroupsCollections } from '@pkg/hooks';
import { useMediaQuery } from '@pkg/providers';

import { GroupFilterNoResults } from '../GroupFilterNoResults';

import { CollectionItem } from './CollectionItem';
import './groupFilter.scss';
import { FadeDiv, GroupFilterCollectionsOverride, LoadMoreButtonWrapper } from './style';

interface Props extends FilterIndex {
    height?: number;
    isPreviousData?: boolean;
    hideMembers?: boolean;
}

// TODO: This file should be removed and consolidated with `CollectionsFilterContent.tsx`.
// The only reason this was forked was to start refactoring to `useNftFilter()` incrementally.
// Any differences between these two files should be reconciled in favour of `CollectionsFilterContent`.
export const CollectionsFilterContentGroups = ({ value, height = 160, hideMembers }: Props) => {
    const [nameToFind, setNameToFind] = useState('');
    const { data, isError, isLoading, hasNextPage, fetchNextPage, isPreviousData, isFetchingNextPage } =
        useGetGroupsCollections(nameToFind);

    const { t } = useTranslation(['groups']);
    const { tablet } = useMediaQuery();
    const { ref, inView } = useInView();

    const [headingRef, { height: headingHeight }] = useMeasure<HTMLDivElement>();
    const filterContentHeight = height - headingHeight - 130;

    useEffect(() => {
        if (inView && !isFetchingNextPage && !isLoading) {
            fetchNextPage();
        }
    }, [inView, isFetchingNextPage, isLoading, fetchNextPage]);

    if (isError) {
        return null;
    }

    const changeName = (newName: string) => {
        setNameToFind(newName);
    };

    const content = getPagesContent(data?.pages);

    const filterMarkup = (
        <GroupFilterCollectionsOverride>
            <FilterContent height={`${filterContentHeight}px`} customPadding={0} scrollBarColor="#523DF1" noBorder>
                <FadeDiv noFade />

                {!content?.length ? <GroupFilterNoResults /> : null}

                {content?.map(({ collection_image, collection_name, contract_address, item_count, member_count }) => (
                    <FilterLink
                        key={contract_address}
                        to={`?${GROUPS_SEARCH_PARAMS_KEYS.collections}=${contract_address}`}
                    >
                        <CollectionItem
                            name={collection_name}
                            avatar={collection_image}
                            assetTotal={item_count}
                            memberTotal={member_count}
                            hideMembers={hideMembers}
                        />
                    </FilterLink>
                ))}

                {hasNextPage ? (
                    <LoadMoreButtonWrapper noFade>
                        <Button
                            ref={ref}
                            fluid
                            loading={isFetchingNextPage}
                            variant="solid"
                            color="translucent"
                            size={tablet ? 'small' : 'medium'}
                            disabled={isPreviousData || !hasNextPage || isFetchingNextPage}
                            onClick={() => fetchNextPage()}
                        >
                            {t('groups_btn_load_more_collections')}
                        </Button>
                    </LoadMoreButtonWrapper>
                ) : null}
            </FilterContent>
        </GroupFilterCollectionsOverride>
    );

    return (
        <AccordionItem value={value}>
            <div ref={headingRef}>
                <FilterHeading disableUpperCase>{t('groups_filter_collections_title')}</FilterHeading>
                <DepInput
                    autoFocus={false}
                    isInvalid={false}
                    classes="group_filter_collection_input"
                    placeholder={t('groups_filter_collections_search')}
                    value={nameToFind}
                    onChange={(e) => changeName(e.target.value)}
                    prepend={<SearchIcon fill="#ffffff" />}
                    append={nameToFind ? <CloseButton onClick={() => changeName('')} fill="#ffffff" /> : null}
                />
            </div>

            {isLoading ? null : filterMarkup}
        </AccordionItem>
    );
};
